import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import VIMData from './VIMData'
import ActiveNode from './ActiveNode'
import Api from '../../api/endPoints'
import { setVimDevices, selectVimDeviceById, mapVimItemToVimData, mapVimItemToActiveNodes } from '../../features/vimData/vimSlice'
import { setBreadcrumb } from '../../features/breadcrumb/breadcrumbSlice'
import { selectAssetProfileId } from '../../features/assetProfile/assetProfileSlice'
import { formatDateTime } from '../../utils/date'
import { useDeviceDashboardSocket, mapDeviceUpdateToVimData, mapNsyncUpdateToActiveNodes } from '../../socket/deviceDashboardSocket'

const pickDefined = (base = {}, override = {}) => {
  const out = { ...base }
  Object.entries(override).forEach(([key, value]) => {
    if (value !== undefined) out[key] = value
  })
  return out
}

// Triggers a browser download of `data` serialized as a pretty-printed JSON file.
const downloadJson = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}

const mergeVimData = (apiVim, socketVim) => {
  if (!apiVim) return socketVim
  if (!socketVim) return apiVim
  return {
    ...pickDefined(apiVim, socketVim),
    connectivity: pickDefined(apiVim.connectivity, socketVim.connectivity),
    metadata: pickDefined(apiVim.metadata, socketVim.metadata),
  }
}

const ViewDashboard = () => {
  const { deviceId } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [view, setView] = useState('overview')
  // Fetched once per session by Layout — null until it resolves.
  const profileID = useSelector(selectAssetProfileId)
  const [isRefetching, setIsRefetching] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const vimDevice = useSelector((state) => selectVimDeviceById(state, deviceId))

  useEffect(() => {
    dispatch(setBreadcrumb([
      { title: 'VIM List', link: '/vim-list' },
      { title: deviceId, link: `/ViewDashboard/${deviceId}`, current: true },
    ]))
  }, [dispatch, deviceId])

  // Always refetches on mount, even if this device is already in redux from
  // an earlier VIM List load — that snapshot's lastSeen/online/etc. can be
  // stale by however long it's been since that fetch, and this page is
  // exactly where a user expects to see the current state.
  useEffect(() => {
    if (!deviceId || profileID == null) return
    const fetchVimDevices = async () => {
      try {
        const response = await Api.getProfileDevices(profileID)
        dispatch(setVimDevices(response.data?.items ?? []))
      } catch (error) {
        console.error('Error fetching VIM devices:', error)
      }
    }
    fetchVimDevices()
  }, [deviceId, profileID, dispatch])


  const MIN_REFETCH_MS = 1200

  const handleRefetch = async () => {
    // Without the profile id the request would resolve to /profiles/null/devices.
    if (isRefetching || profileID == null) return
    setIsRefetching(true)
    const startedAt = Date.now()
    try {
      const response = await Api.getProfileDevices(profileID)
      dispatch(setVimDevices(response.data?.items ?? []))
    } catch (error) {
      console.error('Error refetching VIM devices:', error)
    } finally {
      const remaining = MIN_REFETCH_MS - (Date.now() - startedAt)
      if (remaining > 0) await new Promise((resolve) => setTimeout(resolve, remaining))
      setIsRefetching(false)
    }
  }

  // Exports the device's latest snapshot as raw JSON (device summary + telemetry + nodes).
  const handleJSONExport = async () => {
    if (isExporting || !deviceId) return
    setIsExporting(true)
    try {
      const response = await Api.getDevicesLatest(deviceId)
      const device = response.data
      if (!device?.deviceId) {
        console.warn('No telemetry data to export for device:', deviceId)
        return
      }
      // firstSeen/lastSeen arrive as UTC ISO strings — present them in the
      // user's local time (dd-mm-yyyy HH:mm:ss) in the exported file.
      const exportDevice = {
        ...device,
        ...(device.firstSeen !== undefined && { firstSeen: formatDateTime(device.firstSeen) }),
        ...(device.lastSeen !== undefined && { lastSeen: formatDateTime(device.lastSeen) }),
      }
      downloadJson(exportDevice, `${deviceId}-devices-latest-${new Date().toISOString().replace(/[:.]/g, '-')}.json`)
    } catch (error) {
      console.error('Error exporting telemetry JSON:', error)
    } finally {
      setIsExporting(false)
    }
  }

 
  const device = { name: deviceId, sn: vimDevice?.serial ?? '-' }

  const { data: liveData, nsyncData, connected } = useDeviceDashboardSocket(deviceId)
  const vimData = mergeVimData(mapVimItemToVimData(vimDevice), mapDeviceUpdateToVimData(liveData))
  // API nodes seed the list; the socket's nsyncUpdate takes over once it streams.
  const activeNodes = mapNsyncUpdateToActiveNodes(nsyncData) ?? mapVimItemToActiveNodes(vimDevice)

  
  const ONLINE_STALE_MS = 60 * 1000
  const lastDataAtRef = useRef(null)
  const [, forceTick] = useState(0)

 
  useEffect(() => {
    if (!liveData && !nsyncData) return
    lastDataAtRef.current = Date.now()
    forceTick((n) => n + 1)
  }, [liveData, nsyncData])

  
  useEffect(() => {
    const interval = setInterval(() => forceTick((n) => n + 1), 5000)
    return () => clearInterval(interval)
  }, [])

  const socketHasReported = lastDataAtRef.current !== null
  const socketFresh = socketHasReported && Date.now() - lastDataAtRef.current < ONLINE_STALE_MS
  const isOnline = socketHasReported ? (connected && socketFresh) : Boolean(vimDevice?.online)
  const statusLabel = isOnline ? 'ONLINE' : 'OFFLINE'
  // Live socket tick wins; before the socket connects it's the API item's
  // lastSeen; '-' only when neither has arrived yet. deviceInfo.ts is epoch
  // seconds (see BmsDashboard), so scale to ms for formatDateTime.
  const deviceTs = liveData?.rows?.[0]?.deviceInfo?.ts
  const lastSynced = deviceTs !== undefined
    ? formatDateTime(deviceTs * 1000)
    : formatDateTime(vimDevice?.lastSeen)

  return (
    <div className="w-full h-full bg-[#f2f4f7] flex flex-col">
      <div className="relative h-[54px] bg-[#f9fafa] flex items-center justify-between px-4 border-b border-[#dfe4ed] shrink-0">
        <div className="flex items-center gap-3">
          <button
            className="w-10 h-10 border-none bg-transparent cursor-pointer flex items-center justify-center rounded-lg"
            // Explicit target (matches this page's own breadcrumb parent)
            // rather than navigate(-1) — going back by relative history
            // position breaks as soon as any child page (e.g. Historical
            // Data) has pushed extra entries onto the stack.
            onClick={() => navigate('/vim-list')}
          >
            <span className="material-icons text-xl text-[#272d37]">
              arrow_back
            </span>
          </button>
          <div className="flex flex-col">
            <span className="text-sm font-medium text-[#272d37]">
              {device.name}
            </span>
            <span className="text-sm font-normal text-[#616a79]">
              SN: {device.sn}
            </span>
          </div>
          <div
            className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 ${isOnline ? "bg-[#e6f8e6]" : "bg-[#eceded]"}`}
          >
            <div
              className={`w-3.5 h-3.5 rounded-full flex items-center justify-center ${isOnline ? "bg-[#c0efc0]" : "bg-[#d5d8dc]"}`}
            >
              <div
                className={`w-2 h-2 rounded-full ${isOnline ? "bg-[#27b83e]" : "bg-[#5e6673]"}`}
              />
            </div>
            <span className="text-xs font-medium text-[#272d37]">
              {statusLabel}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-normal text-[#252a34]">
            Last synced at: {lastSynced}
          </span>
          <button
            className="w-10 h-10 border-none bg-transparent cursor-pointer flex items-center justify-center rounded-lg"
            onClick={handleRefetch}
          >
            <span className="material-icons text-xl text-[#272d37]">sync</span>
          </button>
          <button
            className="w-10 h-10 border-none bg-transparent cursor-pointer flex items-center justify-center rounded-lg disabled:opacity-50"
            onClick={handleJSONExport}
            disabled={isExporting}
          >
            <span className="material-symbols-outlined text-xl text-[#272d37]">file_save</span>
          </button>
        </div>
        {isRefetching && (
          <div className="absolute bottom-0 left-0 right-0 h-[2px] overflow-hidden">
            <div className="h-full w-1/3 bg-[#27b83e] animate-refetch-line" />
          </div>
        )}
      </div>

      <div className="flex-1 flex overflow-hidden">
        <VIMData
          data={vimData}
          onViewHistoricalData={() => navigate(`/ViewDashboard/${deviceId}/historical`)}
        />
        <ActiveNode deviceId={deviceId} nodes={activeNodes} />
      </div>
    </div>
  );
}

export default ViewDashboard
